// src/components/ApiKeys/index.jsx
// Gestao das chaves da API publica (nivel 3). Estado local: tela autocontida,
// sem provider global (providers do projeto sao para estado entre telas).
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/userProvider';
import apiUser from '../../apiUser';
import DataTable from '../Table';
import Loader from '../Loader/load';
import BaseButton from '../General/FormsComp/Button/BaseButton';
import CriarChaveModal from './CriarChaveModal';
import RequestsDrawer from './RequestsDrawer';
import { baixarDocMd, baixarDocPdf } from './docsDownload';

const ApiKeys = () => {
  const { setAlert } = useContext(UserContext);
  const [chaves, setChaves] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalCriar, setModalCriar] = useState(false);
  const [chaveDrawer, setChaveDrawer] = useState(null);
  const [baixandoDoc, setBaixandoDoc] = useState(false);

  const aviso = useCallback((msg) => setAlert({
    visible: true, title: 'Atenção!', placeholder: msg, confirm: false, type: 'warning',
  }), [setAlert]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiUser.get('/apikeys/all');
      const { success, dados, message } = response.data;
      if (success) setChaves(dados.map((c) => ({ ...c, escopos_texto: (c.escopos || []).join(' ') })));
      else aviso(message);
    } catch (error) {
      console.log(error);
      aviso('Erro ao carregar as chaves');
    } finally {
      setLoading(false);
    }
  }, [aviso]);

  useEffect(() => { carregar(); }, [carregar]);

  const revogar = (chave) => {
    setAlert({
      type: 'confirm', visible: true, confirm: true, title: 'Atenção!',
      placeholder: `Revogar a chave "${chave.nome}" (${chave.key_prefix}…)? Ação irreversível — o sistema que a usa perde o acesso na hora.`,
      callback: async () => {
        try {
          const response = await apiUser.post('/apikeys/revogar', { id: chave.id });
          const { success, message } = response.data;
          if (!success) return aviso(message);
          carregar();
        } catch (error) {
          console.log(error);
          aviso('Erro ao revogar a chave');
        }
      },
    });
  };

  const baixarDoc = async (baixar) => {
    setBaixandoDoc(true);
    try {
      await baixar();
    } catch (error) {
      console.log(error);
      aviso('Não foi possível baixar a documentação');
    } finally {
      setBaixandoDoc(false);
    }
  };

  const headers = [
    { label: 'Nome', key: 'nome', sortable: true, width: 'w-2/12', truncate: true },
    {
      label: 'Prefixo', key: 'key_prefix', sortable: false, width: 'w-1/12',
      render: (item) => <code>{item.key_prefix}…</code>,
    },
    { label: 'Usuário', key: 'usuario_nome', sortable: true, width: 'w-2/12', truncate: true },
    {
      label: 'Escopos', key: 'escopos_texto', sortable: false, width: 'w-3/12',
      render: (item) => (
        <div className="flex flex-wrap gap-1">
          {(item.escopos || []).map((escopo) => (
            <span key={escopo} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-[10px] font-mono">
              {escopo}
            </span>
          ))}
        </div>
      ),
    },
    {
      label: 'Último uso', key: 'last_used_at', sortable: true, width: 'w-2/12',
      render: (item) => (item.last_used_at ? new Date(item.last_used_at).toLocaleString('pt-BR') : 'Nunca'),
    },
    {
      label: 'Status', key: 'ativo', sortable: true, width: 'w-1/12',
      render: (item) => (Number(item.ativo) === 1 ? (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Ativa</span>
      ) : (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">Revogada</span>
      )),
    },
    {
      label: 'Ações', key: 'acoes', sortable: false, width: 'w-1/12',
      render: (item) => (
        <div className="flex gap-2">
          <BaseButton variant="ghost" size="xs" onClick={() => setChaveDrawer(item)}>Requests</BaseButton>
          {Number(item.ativo) === 1 && (
            <BaseButton variant="danger" size="xs" onClick={() => revogar(item)}>Revogar</BaseButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="w-full min-h-full p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Chaves de API</h1>
        <div className="flex flex-wrap gap-2">
          <BaseButton variant="outline" size="sm" disabled={baixandoDoc} onClick={() => baixarDoc(baixarDocMd)}>
            Baixar doc (.md)
          </BaseButton>
          <BaseButton variant="outline" size="sm" disabled={baixandoDoc} onClick={() => baixarDoc(baixarDocPdf)}>
            Baixar doc (.pdf)
          </BaseButton>
          <BaseButton size="sm" onClick={() => setModalCriar(true)}>Gerar chave</BaseButton>
        </div>
      </div>
      {loading || chaves === null ? (
        <Loader />
      ) : chaves.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-10">
          Nenhuma chave criada ainda. Gere a primeira no botão acima.
        </p>
      ) : (
        <DataTable data={chaves} headers={headers} perPageItems={10} />
      )}
      {modalCriar && <CriarChaveModal onClose={() => setModalCriar(false)} onCriada={carregar} />}
      {chaveDrawer && <RequestsDrawer key={chaveDrawer.id} chave={chaveDrawer} onClose={() => setChaveDrawer(null)} />}
    </div>
  );
};

export default ApiKeys;
